import React, { useEffect, useState } from 'react';
import { Cpu, Trash2, Check, Copy, AlertTriangle, ShieldCheck, ExternalLink } from 'lucide-react';
import { useI18n } from '@/context/I18nContext';
import { useToast } from '@/context/ToastContext';
import { rpc } from '@/api/client';

interface AuthorizedApp {
  id: string;
  user_id: string;
  client_id: string;
  client_name: string;
  scope: string;
  created_at: string;
  last_used_at?: string;
}

export function ConnectedAppsSection() {
  const { t } = useI18n();
  const { showToast } = useToast();

  const [apps, setApps] = useState<AuthorizedApp[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmRevokeApp, setConfirmRevokeApp] = useState<AuthorizedApp | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  const fetchApps = async () => {
    setIsLoading(true);
    try {
      const res = await rpc<{ apps?: AuthorizedApp[] }>('ListAuthorizedApps', {});
      setApps(res?.apps || []);
    } catch (err: any) {
      console.error('Failed to load authorized apps', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchApps();
  }, []);

  const handleRevokeApp = async () => {
    if (!confirmRevokeApp) return;
    setIsRevoking(true);
    try {
      await rpc('RevokeAuthorizedApp', { id: confirmRevokeApp.id });
      showToast(t('settings.appRevokedSuccess'), 'success');
      setConfirmRevokeApp(null);
      await fetchApps();
    } catch (err: any) {
      showToast(err?.message || 'Failed to revoke application access', 'error');
    } finally {
      setIsRevoking(false);
    }
  };

  const copyToClipboard = (text: string, setCopied: (val: boolean) => void) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast(t('settings.keyCopied'), 'success');
    });
  };

  const apiUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8080';
  const mcpConfigSnippet = JSON.stringify(
    {
      mcpServers: {
        'jolly-tesla': {
          url: `${apiUrl}/mcp/sse`,
        },
      },
    },
    null,
    2
  );

  return (
    <div className="card p-5 space-y-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-accent/10 text-accent shrink-0 mt-0.5">
          <ShieldCheck size={18} />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-text-primary">{t('settings.connectedApps')}</h4>
          <p className="text-xs text-text-muted mt-0.5 max-w-xl">{t('settings.connectedAppsDesc')}</p>
        </div>
      </div>

      {/* Apps List */}
      {isLoading ? (
        <div className="text-xs text-text-muted py-4 text-center">{t('common.loading')}</div>
      ) : apps.length === 0 ? (
        <div className="p-4 rounded-lg bg-bg-surface border border-border-subtle text-xs text-text-muted text-center">
          {t('settings.noConnectedApps')}
        </div>
      ) : (
        <div className="space-y-2.5">
          {apps.map((app) => (
            <div
              key={app.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 rounded-lg bg-bg-surface border border-border-default gap-3"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-accent/10 text-accent">
                  <Cpu size={16} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-text-primary">{app.client_name}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[0.7rem] text-text-muted">
                    <span>
                      {t('settings.authorizedOn')}: {new Date(app.created_at).toLocaleDateString()}
                    </span>
                    <span>•</span>
                    <span>
                      {t('settings.lastUsed')}:{' '}
                      {app.last_used_at ? new Date(app.last_used_at).toLocaleDateString() : t('settings.neverUsed')}
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setConfirmRevokeApp(app)}
                className="self-end sm:self-center px-2.5 py-1 rounded text-xs font-medium text-text-secondary hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-colors cursor-pointer flex items-center gap-1.5"
                title={t('settings.revoke')}
              >
                <Trash2 size={13} />
                <span>{t('settings.revoke')}</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* MCP Configuration Snippet Box */}
      <div className="p-4 rounded-lg bg-bg-surface border border-border-subtle space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-medium text-text-primary">
            <ExternalLink size={14} className="text-accent" />
            <span>{t('settings.mcpConfigSnippet')}</span>
          </div>
          <button
            type="button"
            onClick={() => copyToClipboard(mcpConfigSnippet, setCopiedSnippet)}
            className="px-2 py-1 rounded text-xs text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer flex items-center gap-1"
          >
            {copiedSnippet ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
            <span>{copiedSnippet ? t('settings.keyCopied') : t('settings.copyKey')}</span>
          </button>
        </div>
        <p className="text-xs text-text-muted">{t('settings.mcpConfigDesc')}</p>
        <pre className="mono text-xs p-3 rounded bg-bg-base border border-border-subtle text-text-secondary overflow-x-auto">
          {mcpConfigSnippet}
        </pre>
      </div>

      {/* Revoke Confirmation Modal */}
      {confirmRevokeApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs animate-fade-in">
          <div className="card w-full max-w-md p-5 space-y-4 shadow-xl border border-border-default bg-bg-raised">
            <h3 className="text-base font-semibold text-text-primary flex items-center gap-2 text-red-400">
              <AlertTriangle size={18} />
              {t('settings.revoke')} &ldquo;{confirmRevokeApp.client_name}&rdquo;?
            </h3>
            <p className="text-xs text-text-secondary leading-relaxed">
              {t('settings.revokeAppConfirm')}
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                disabled={isRevoking}
                onClick={() => setConfirmRevokeApp(null)}
                className="px-3.5 py-1.5 rounded-md text-xs font-medium text-text-secondary hover:text-text-primary border border-border-default hover:bg-bg-hover transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={isRevoking}
                onClick={handleRevokeApp}
                className="px-4 py-1.5 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-50 transition-colors cursor-pointer flex items-center gap-1.5"
              >
                {isRevoking ? t('common.loading') : t('settings.revoke')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
